import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  input,
  Output,
  signal
} from '@angular/core';

import {
  CalendarMonth,
  CalendarViewer,
  DateRange,
  DateRangeSelection,
  FiscalMonth,
  ViewerType,
  WEEK_DAYS
} from '@design-system/components/m-fiscal-calendar/m-fiscal-calendar.component.types';
import {
  CalendarBuilder,
  DateUtils,
  MonthSpanCalculator
} from '@design-system/components/m-fiscal-calendar/m-fiscal-calendar.utils';

@Component({
  selector: 'm-fiscal-calendar',
  templateUrl: './m-fiscal-calendar.component.html',
  styleUrls: ['./m-fiscal-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MFiscalCalendarComponent {
  months = input.required<FiscalMonth[]>();
  startDate = input.required<string>();
  endDate = input.required<string>();
  previousStartDate = input<string | null>(null);
  previousEndDate = input<string | null>(null);
  minDate = input<string | null>(null);
  maxDate = input<string | null>(null);
  selectionMode = input<'range' | 'single'>('range');

  @Output() selectedDateRange = new EventEmitter<DateRangeSelection>();

  readonly weekDays = WEEK_DAYS;
  readonly monthSpanCalculator = new MonthSpanCalculator();

  private readonly dateSelection = signal<Record<ViewerType, DateRange>>({
    current: { start: null, end: null },
    previous: { start: null, end: null }
  });

  readonly compareMode = computed(
    () => this.previousStartDate() !== null && this.previousEndDate() !== null
  );

  readonly fiscalCalendarData = computed<CalendarMonth[]>(() => {
    const months = this.months();
    return CalendarBuilder.buildFromMonths(months);
  });

  readonly viewers = computed<CalendarViewer[]>(() => {
    const possibleViewers = this.compareMode()
      ? ['current', 'previous']
      : ['current'];
    return possibleViewers.map((type) => {
      const windowSize = this.compareMode() ? 1 : 2;
      const startDate =
        type === 'previous' ? this.previousStartDate() : this.startDate();
      return {
        viewerType: type as ViewerType,
        startIndex: this.showMonthsBasedOnSelectedDates(startDate, windowSize),
        windowSize
      };
    });
  });

  // Public viewer-based navigation methods for template use
  getVisibleMonths(viewer: CalendarViewer): CalendarMonth[] {
    const data = this.fiscalCalendarData();
    return data.slice(viewer.startIndex, viewer.startIndex + viewer.windowSize);
  }

  canNavigateLeft(viewer: CalendarViewer): boolean {
    return viewer.startIndex > 0;
  }

  canNavigateRight(viewer: CalendarViewer): boolean {
    const data = this.fiscalCalendarData();
    return viewer.startIndex + viewer.windowSize < data.length;
  }

  constructor() {
    effect(() => {
      this.months();
      this.monthSpanCalculator.clearCache();
    });

    effect(
      () => {
        const start = this.startDate();
        const end = this.endDate();
        const previousStart = this.previousStartDate();
        const previousEnd = this.previousEndDate();

        if (!start || !end) return;

        this.dateSelection.set({
          current: { start, end },
          previous: { start: previousStart, end: previousEnd }
        });
      },
      { allowSignalWrites: true }
    );
  }

  // Public viewer-based navigation methods
  navigateLeft(viewer: CalendarViewer): void {
    if (!this.canNavigateLeft(viewer)) return;
    viewer.startIndex -= 1;
  }

  navigateRight(viewer: CalendarViewer): void {
    if (!this.canNavigateRight(viewer)) return;
    viewer.startIndex += 1;
  }

  onDateClick(dateStr: string, viewerType: ViewerType): void {
    if (!dateStr || this.isDateDisabled(dateStr)) return;

    if (this.selectionMode() === 'single') {
      this.updateDateSelection({ start: dateStr, end: dateStr }, viewerType);
      this.emitSelection();
      return;
    }

    const { start, end } = this.getSelectedDateRange(viewerType);
    const nextRange = this.calculateNextRange(dateStr, start, end);

    this.updateDateSelection(nextRange, viewerType);
    this.emitSelection();
  }

  getDayClasses(
    day: string,
    viewerType: ViewerType,
    week: string[],
    dayIdx: number
  ): Record<string, boolean> {
    const { start, end } = this.getSelectedDateRange(viewerType);
    const inRange =
      day && start && end
        ? DateUtils.parse(day) > DateUtils.parse(start) &&
          DateUtils.parse(day) < DateUtils.parse(end)
        : false;

    return {
      empty: !day,
      disabled: this.isDateDisabled(day),
      selected: day === start || day === end,
      'in-range': inRange,
      'start-date': day === start,
      'end-date': day === end,
      'new-month-start': this.monthSpanCalculator.isNewMonthStart(week, dayIdx),
      'in-new-month': this.monthSpanCalculator.isInNewMonth(week, dayIdx)
    };
  }

  private isDateDisabled(dateStr: string): boolean {
    if (!dateStr) return false;

    const date = DateUtils.parse(dateStr);
    const min = this.minDate();
    const max = this.maxDate();

    if (min && date < DateUtils.parse(min)) return true;
    if (max && date > DateUtils.parse(max)) return true;

    return false;
  }

  getDayOfMonth(dateStr: string): string {
    return DateUtils.getDay(dateStr);
  }

  isEmptyWeek(week: string[]): boolean {
    return !week.some(Boolean);
  }

  private getSelectedDateRange(viewerType: ViewerType): DateRange {
    return this.dateSelection()[viewerType as ViewerType];
  }

  private calculateNextRange(
    clickedDate: string,
    currentStart: string | null,
    currentEnd: string | null
  ): DateRange {
    if (!currentStart || currentEnd) {
      return { start: clickedDate, end: null };
    }

    if (DateUtils.parse(clickedDate) < DateUtils.parse(currentStart)) {
      return { start: clickedDate, end: null };
    }

    return { start: currentStart, end: clickedDate };
  }

  private updateDateSelection(range: DateRange, viewerType: ViewerType): void {
    this.dateSelection.update((state) => ({ ...state, [viewerType]: range }));
  }

  private showMonthsBasedOnSelectedDates(
    startDate: string | null,
    windowSize: number
  ): number {
    if (!startDate) return 0;
    const data = this.fiscalCalendarData();
    const selectedStartDateMonthIndex = data.findIndex((m) =>
      m.days.includes(startDate)
    );
    if (selectedStartDateMonthIndex < 0) return 0;

    const lastValidStartIndex = Math.max(0, data.length - windowSize);
    return Math.min(selectedStartDateMonthIndex, lastValidStartIndex);
  }

  private emitSelection(): void {
    const { current, previous } = this.dateSelection();

    if (!current.start) return;

    const hasPrevious = previous.start && previous.end;

    if (this.compareMode() && !hasPrevious) return;

    this.selectedDateRange.emit({
      startDate: DateUtils.parse(current.start),
      endDate: current.end ? DateUtils.parse(current.end) : null,
      previousStartDate: hasPrevious ? DateUtils.parse(previous.start!) : null,
      previousEndDate: hasPrevious ? DateUtils.parse(previous.end!) : null
    });
  }

  getRange(start: number, end: number): number[] {
    return Array.from({ length: end - start }, (_, i) => start + i);
  }
}
